/**
 * Taxonomia do site: categorias "guarda-chuva" (o que a cliente vê) e as
 * subcategorias reais gravadas em `products.category`.
 *
 * Tudo é estático (sem requisição) para manter a navegação instantânea.
 */

export interface SubCategory {
  /** Nome exatamente como está em products.category */
  category: string;
  slug: string;
  label: string;
}

export interface ParentCategory {
  slug: string;
  name: string;
  /** Grupo do menu ao qual pertence */
  group: string;
  subs: SubCategory[];
}

const sub = (category: string, slug: string, label = category): SubCategory => ({
  category,
  slug,
  label,
});

export const CATEGORY_TREE: ParentCategory[] = [
  {
    slug: "blusas",
    name: "Blusas",
    group: "parte-de-cima",
    subs: [
      sub("Blusas", "blusas-manga", "Blusas"),
      sub("Camisas", "camisas"),
      sub("T-Shirts", "t-shirts"),
      sub("Regatas", "regatas"),
      sub("Croppeds", "croppeds"),
      sub("Bodies", "bodies"),
    ],
  },
  {
    slug: "casacos",
    name: "Casacos e Jaquetas",
    group: "parte-de-cima",
    subs: [
      sub("Jaquetas e Casacos", "jaquetas-e-casacos"),
      sub("Blazers", "blazers"),
      sub("Coletes", "coletes"),
    ],
  },
  {
    slug: "calcas",
    name: "Calças",
    group: "parte-de-baixo",
    subs: [sub("Calças", "calcas-todas", "Calças")],
  },
  {
    slug: "shorts",
    name: "Shorts e Bermudas",
    group: "parte-de-baixo",
    subs: [
      sub("Shorts", "shorts-curto", "Shorts"),
      sub("Shorts Saia", "shorts-saia"),
      sub("Bermudas", "bermudas"),
    ],
  },
  {
    slug: "saias",
    name: "Saias",
    group: "parte-de-baixo",
    subs: [
      sub("Saias Curtas", "saias-curtas", "Curtas"),
      sub("Saias Midi", "saias-midi", "Midi"),
      sub("Saias Longas", "saias-longas", "Longas"),
    ],
  },
  {
    slug: "vestidos",
    name: "Vestidos",
    group: "peca-unica",
    subs: [
      sub("Vestidos Curtos", "vestidos-curtos", "Curtos"),
      sub("Vestidos Midi", "vestidos-midi", "Midi"),
      sub("Vestidos Longos", "vestidos-longos", "Longos"),
    ],
  },
  {
    slug: "conjuntos",
    name: "Conjuntos",
    group: "peca-unica",
    subs: [sub("Conjuntos", "conjuntos-todos", "Conjuntos")],
  },
  {
    slug: "macacoes",
    name: "Macacões",
    group: "peca-unica",
    subs: [sub("Macacões", "macacoes-longos", "Macacões"), sub("Macaquinhos", "macaquinhos")],
  },
  {
    slug: "acessorios",
    name: "Acessórios",
    group: "acessorios",
    subs: [
      sub("Bolsas", "bolsas"),
      sub("Cintos", "cintos"),
      sub("Brincos", "brincos"),
      sub("Colares", "colares"),
      sub("Pulseiras", "pulseiras"),
      sub("Lenços e Cachecóis", "lencos-e-cachecois", "Lenços"),
    ],
  },
];

export const CATEGORY_GROUP_NAMES: Record<string, string> = {
  "parte-de-cima": "Parte de Cima",
  "parte-de-baixo": "Parte de Baixo",
  "peca-unica": "Peça Única",
  acessorios: "Acessórios",
};

const PARENT_BY_SLUG = new Map(CATEGORY_TREE.map((p) => [p.slug, p]));

const SUB_BY_SLUG = new Map<string, { parent: ParentCategory; sub: SubCategory }>();
CATEGORY_TREE.forEach((p) =>
  p.subs.forEach((s) => {
    if (!PARENT_BY_SLUG.has(s.slug)) SUB_BY_SLUG.set(s.slug, { parent: p, sub: s });
  }),
);

/** slugs antigos (usados em links/banners salvos) -> slug novo */
export const LEGACY_SLUG_MAP: Record<string, string> = {
  "vestidos-curtos": "vestidos-curtos",
  camisetas: "t-shirts",
  jaquetas: "jaquetas-e-casacos",
  "saias-midi": "saias-midi",
};

export interface ResolvedCategory {
  /** slug canônico da página */
  slug: string;
  title: string;
  /** categorias reais para filtrar no banco */
  categories: string[];
  /** subcategorias exibidas no filtro desta página */
  subs: SubCategory[];
  parent?: ParentCategory;
  isParent: boolean;
}

/** Resolve o slug da URL para a categoria (pai ou subcategoria). */
export function resolveCategorySlug(raw?: string | null): ResolvedCategory | null {
  const slug = (raw || "").trim().toLowerCase();
  if (!slug) return null;

  const mapped = LEGACY_SLUG_MAP[slug] || slug;

  const parent = PARENT_BY_SLUG.get(mapped);
  if (parent) {
    return {
      slug: parent.slug,
      title: parent.name,
      categories: parent.subs.map((s) => s.category),
      subs: parent.subs.length > 1 ? parent.subs : [],
      parent,
      isParent: true,
    };
  }

  const found = SUB_BY_SLUG.get(mapped);
  if (found) {
    return {
      slug: found.sub.slug,
      title: found.sub.category,
      categories: [found.sub.category],
      subs: found.parent.subs.length > 1 ? found.parent.subs : [],
      parent: found.parent,
      isParent: false,
    };
  }

  return null;
}

/** Categoria real (products.category) -> categoria pai. */
const PARENT_BY_CATEGORY = new Map<string, ParentCategory>();
CATEGORY_TREE.forEach((p) => p.subs.forEach((s) => PARENT_BY_CATEGORY.set(s.category, p)));

export const parentOfCategory = (category?: string | null) =>
  category ? PARENT_BY_CATEGORY.get(category) ?? null : null;

/** URL da página de uma categoria (sempre /categoria/...). */
export const categoryUrl = (slug: string) => `/categoria/${slug}`;

export const MENU_TREE = Object.keys(CATEGORY_GROUP_NAMES).map((groupId) => ({
  id: groupId,
  name: CATEGORY_GROUP_NAMES[groupId],
  parents: CATEGORY_TREE.filter((p) => p.group === groupId),
}));
